import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  getGanttAccessRoles,
  saveGanttAccessRoles,
} from '../../../../packages/common/api';
import Page from '../../../../packages/common/shared-ui/src/components/Page';
import CheckboxIcon from '../../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DeleteIcon from '../../../../packages/common/shared-ui/src/icons/DeleteIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';

// Original class: blockWrapper
const StyledBlockWrapper = styled(Box)(theme => ({
  width: '100%',
  padding: '24px 32px',
  borderRadius: '16px',
  backgroundColor: '#fff',
}));
// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(theme => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    position: 'static',
    width: '200px',
    color: '#41424E',
    lineHeight: '20px',
    transform: 'none',
    display: 'flex',
    flexShrink: 0,
    '& .MuiFormLabel-asterisk': {
      color: '#E03737',
    },
    '&.Mui-focused': {
      transform: 'none',
      left: '10px',
      top: '15px',
    },
  },
}));
// Original class: filterSelect
const StyledFilterSelect = styled(Autocomplete, {
  shouldForwardProp: prop => !['multipleStyle'].includes(prop),
})(({ theme, multipleStyle }) => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '8px 65px 8px 16px',
    lineHeight: '20px',
    height: 'auto',
    ...(multipleStyle && {
      minHeight: '36px',
      padding: '0 65px 0 0',
    }),
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#212346',
      padding: '0',
      height: 'auto',
      ...(multipleStyle && {
        marginLeft: '16px',
      }),
    },
    '&.Mui-disabled': {
      '& .MuiAutocomplete-input': {
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root path': {
          fill: '#E4E4EF',
        },
      },
    },
  },

  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    minWidth: 'auto!important',
    width: '100%!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      border: '1px solid #E4E4EF',
      borderTop: 'none',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: chip
const StyledChip = styled(Chip)(theme => ({
  '&.MuiChip-root': {
    height: '30px',
    margin: '3px 4px',
    fontSize: '12px',
    display: 'flex',
    border: '1px solid #E4E4EF',
    borderRadius: '14px',
    '& .MuiChip-label': {
      padding: '0 8px',
    },
    '& .MuiChip-deleteIcon': {
      position: 'static',
      height: '10px !important',
    },
  },
}));
const GanttSettings = () => {
  const getLocalString = useTranslation();
  const pageTitle = getLocalString('AdminGantt', 'title');
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [initialSettings, setInitialSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveSettingsloading, setSaveSettingsloading] = useState(false);
  const [settingsChanged, setSettingsChanged] = useState(false);
  const handleIssueError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );

  useEffect(() => {
    let ignore = false;
    async function getAccessRoles() {
      const { data: roles } = await getGanttAccessRoles();
      if (!ignore) {
        return roles;
      }
    }
    getAccessRoles()
      .then(res => {
        setInitialSettings(res);
        setSettings(res);
        setLoading(false);
      })
      .catch(error => handleIssueError(error.response));
    return () => {
      ignore = true;
    };
  }, [handleIssueError]);

  const saveConfirmationSettings = useCallback(async () => {
    setSaveSettingsloading(true);
    try {
      const data = Object.entries(settings).reduce((acc, item) => {
        acc[item[0]] = item[1]
          .filter(item => item.isChecked)
          .map(item => item.valueId.toString());
        return acc;
      }, {});
      const { data: settingsData } = await saveGanttAccessRoles(data);
      if (settingsData) {
        setInitialSettings(settings);
        setSaveSettingsloading(false);
        setSettingsChanged(false);
      }
    } catch (error) {
      setSaveSettingsloading(false);
      handleIssueError(error.response);
    }
  }, [handleIssueError, settings]);

  const handleCancel = useCallback(() => {
    setSettings(initialSettings);
    setSettingsChanged(false);
  }, [initialSettings]);

  const handleChange = useCallback(
    (value, type) => {
      setSettings({
        ...settings,
        [type]: settings[type].map(item => {
          const currentItem = value.find(el => el.valueId === item.valueId);
          if (currentItem !== undefined) {
            return { ...item, isChecked: true };
          } else {
            return { ...item, isChecked: false };
          }
        }),
      });
      setSettingsChanged(true);
    },
    [settings],
  );
  return (
    <>
      {loading ? (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '70px',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      ) : (
        <Page title={pageTitle} width='100%'>
          <Grid
            container
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography
                variant='h1'
                sx={{
                  m: 0,
                  padding: '10px 0',
                }}
              >
                {pageTitle}
              </Typography>
            </Grid>
            <Grid>
              <Grid>
                <Button
                  variant='defaultGreyPrimary'
                  disableRipple
                  onClick={saveConfirmationSettings}
                  disabled={!settingsChanged}
                >
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    {getLocalString('buttons', 'save')}
                  </Typography>
                </Button>
                <Button
                  onClick={handleCancel}
                  variant='defaultGreyPrimary'
                  disableRipple
                  sx={{ marginLeft: '8px' }}
                >
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    {getLocalString('buttons', 'cancel')}
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={5} position={'relative'}>
            {Object.entries(settings).map((item, index) => {
              return (
                <Grid size={'grow'} key={index}>
                  <StyledBlockWrapper
                    sx={{
                      padding: '0 28px 20px 28px',
                      marginTop: '16px',
                      position: 'relative',
                    }}
                  >
                    <Grid container>
                      <FormControl
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                        }}
                      >
                        <StyledSelectLabel
                          sx={{ marginTop: '16px', width: '100%!important' }}
                        >
                          <Typography
                            variant='h5'
                            sx={{
                              color: '#41424E',
                            }}
                          >
                            {getLocalString(
                              'AdminGantt',
                              'labels',
                              item[0].slice(0, -1),
                            )}
                          </Typography>
                        </StyledSelectLabel>
                        <StyledFilterSelect
                          multipleStyle={true}
                          multiple
                          onChange={(event, newValue) =>
                            handleChange(newValue, item[0])
                          }
                          noOptionsText={getLocalString('labels', 'notFound')}
                          options={item[1]}
                          getOptionLabel={option => option.valueName || ''}
                          disableClearable
                          disableCloseOnSelect
                          disablePortal
                          value={item[1].filter(item => item.isChecked)}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <StyledChip
                                {...getTagProps({ index })}
                                key={index}
                                deleteIcon={
                                  <DeleteIcon
                                    width={10}
                                    height={10}
                                    viewBox='0 0 10 10'
                                  />
                                }
                                variant='outlined'
                                label={option.valueName}
                              />
                            ))
                          }
                          renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.valueId}>
                              <Checkbox
                                icon=<CheckboxIcon
                                  width='12'
                                  height='12'
                                  viewBox='0 0 12 12'
                                />
                                checkedIcon=<CheckboxIconChecked
                                  width='12'
                                  height='12'
                                  viewBox='0 0 12 12'
                                />
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              {option.valueName}
                            </li>
                          )}
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder={getLocalString('labels', 'input')}
                            />
                          )}
                          popupIcon={
                            <ChevronIcon direction='down'></ChevronIcon>
                          }
                          slots={{
                            popper: props => (
                              <Popper
                                {...props}
                                sx={{ maxWidth: '100%!important' }}
                                placement='bottom-start'
                              ></Popper>
                            ),
                          }}
                        />
                      </FormControl>
                    </Grid>
                  </StyledBlockWrapper>
                </Grid>
              );
            })}
            {saveSettingsloading && (
              <Grid
                container
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  zIndex: 99,
                  position: 'absolute',
                }}
              >
                <CircularProgress color='secondary' />
              </Grid>
            )}
          </Grid>
        </Page>
      )}
    </>
  );
};
export default GanttSettings;
